import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import api, { errMsg } from "@/lib/api";

const EMPTY = { name: "", url: "", tag: "Umum", notes: "", fb_account: "", needs_approval: false };

export default function GroupDialog({ group, onClose, onSaved, accounts = [] }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (group) setForm({ ...EMPTY, ...group }); }, [group]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { name: form.name, url: form.url.trim(), tag: form.tag || "Umum", notes: form.notes, fb_account: (form.fb_account || "").trim(), needs_approval: !!form.needs_approval };
    try {
      if (group?.id) await api.put(`/groups/${group.id}`, body);
      else await api.post("/groups", body);
      toast.success("Group disimpan");
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!group} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-border bg-card" data-testid="group-dialog">
        <DialogHeader>
          <DialogTitle>{group?.id ? "Edit group" : "Tambah group"}</DialogTitle>
          <DialogDescription>Salin pautan group dari bar alamat pelayar.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2">
            <Label>Nama group</Label>
            <Input required value={form.name} onChange={set("name")} placeholder="cth: Usahawan Online Malaysia" data-testid="group-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Pautan group</Label>
            <Input required value={form.url} onChange={set("url")} placeholder="https://www.facebook.com/groups/..." data-testid="group-url-input" />
          </div>
          <div className="space-y-2">
            <Label>Kategori</Label>
            <Input value={form.tag} onChange={set("tag")} placeholder="cth: Niaga, Komuniti" data-testid="group-tag-input" />
          </div>
          <div className="space-y-2">
            <Label>Akaun Facebook (yang sertai group ini)</Label>
            <Input value={form.fb_account || ""} onChange={set("fb_account")} list="pp-fb-accounts" maxLength={60} placeholder="cth: Akaun Ali, Akaun Kedai" data-testid="group-fb-account-input" />
            <datalist id="pp-fb-accounts">{accounts.map((a) => <option key={a} value={a} />)}</datalist>
          </div>
          <div className="space-y-2">
            <Label>Nota (peraturan group, hari dibenarkan promosi...)</Label>
            <Textarea value={form.notes} onChange={set("notes")} rows={3} data-testid="group-notes-input" />
          </div>
          <label className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium">Post perlu kelulusan admin</p>
              <p className="text-xs text-muted-foreground">Selepas anda post, status jadi “Menunggu admin”</p>
            </div>
            <Switch checked={!!form.needs_approval} onCheckedChange={(v) => setForm({ ...form, needs_approval: v })} data-testid="group-needs-approval-switch" />
          </label>
          <Button type="submit" disabled={busy} className="w-full" data-testid="save-group-modal-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
